import React from 'react';
import HeaderSection from '../content/HeaderSection';
import FooterSection from '../content/FooterSection';
import useGoogleAnalytics from '../hooks/useGoogleAnalytics';
import { usePageLanguage } from '../context/PageLanguageContext';
import SEOHead from '../components/SEOHead';

// Translation object
const translations = {
  en: {
    pageTitle: 'Video Tutorials - Rink Hockey Scoreboard',
    pageDescription: 'Watch video tutorials to learn how to use the Rink Hockey Scoreboard. Step-by-step guides for setup, game management, and entertainment features.',
    title: 'Video Tutorials',
    subtitle: 'Here you will find short videos explaining how to use the scoreboard. Please also read the',
    manual: 'manual',
    noVideo: 'No video URL configured'
  },
  de: {
    pageTitle: 'Videoanleitungen - Rink Hockey Scoreboard',
    pageDescription: 'Schauen Sie sich Videoanleitungen an, um die Verwendung des Rink Hockey Scoreboards zu erlernen. Schritt-für-Schritt-Anleitungen für Setup, Spielverwaltung und Entertainment-Funktionen.',
    title: 'Videoanleitungen',
    subtitle: 'Hier findest du Kurzvideos die dir die Verwendung vom Scoreboard erklären. Bitte lies dazu auch die',
    manual: 'Anleitungen',
    noVideo: 'Keine Video-URL konfiguriert'
  }
};

const guides = [
  { id: 1, title: 'Einrichten des Scoreboards vor dem Match', loomUrl: 'https://www.loom.com/share/8f1804896a644d849cca91c48b546106?sid=8d19ab36-5200-4f99-abee-f2ba2b27d19a' },
  { id: 2, title: 'Spielvorbereitung und grundlegende Bedienung', loomUrl: 'https://www.loom.com/share/70e71251b03d467ca39e25465c7c7143?sid=d7d95f3d-34db-4e2a-bb2d-078b90aa23f7' },
  { id: 3, title: 'Teamfouls & persönliche Strafen', loomUrl: 'https://www.loom.com/share/6d32f1b17b844eba8785eee7da1ec119?sid=d626e7c2-41e3-470b-8b27-63f9c190a6e2' },
  { id: 4, title: 'Timeouts korrekt durchgeführt', loomUrl: 'https://www.loom.com/share/064e3d4bb2084b33a09a930e2087ff0d?sid=5f48c133-7616-4b42-abfa-1e39b5683d07' },
  { id: 5, title: 'Versteckte aber nützliche Funktionen', loomUrl: 'https://www.loom.com/share/6c89cf980b7849d4bc7d751d7823be06?sid=d0bea834-0bfa-4bfe-8d7c-1b0225300791' },
  { id: 6, title: 'Einstellungen des Entertainment Systems', loomUrl: 'https://www.loom.com/share/3784c78cc4f74477bfe8295f23d67c9d?sid=25a865ad-71ea-4507-b89b-d9486402b231' },
  { id: 7, title: 'Musik vor dem Spiel', loomUrl: 'https://www.loom.com/share/b968e02a048249908614115bf9792538?sid=3c3d4e9f-152c-4796-8e4c-b37d63ad324e' },
  { id: 8, title: 'Abspielen von Filmen', loomUrl: 'https://www.loom.com/share/84af347f554e4923a3820ba1e617999b?sid=0f41f273-e070-4c30-9a8a-31f30b1faed3' },
  { id: 9, title: 'In-Game-Musik für ein besseres Spielerlebnis', loomUrl: 'https://www.loom.com/share/8d03b23cc54c4ebf8dd7667f960f9a14?sid=12cd9817-3b00-4ebf-9e80-7a3ffd8f0252' },
];

const VideoCard = ({ title, loomUrl, noVideoText }) => {
  const toEmbedUrl = (url) => {
    if (!url) return '';
    try {
      const u = new URL(url);
      // Convert share links to embed links to avoid X-Frame-Options: deny
      if (u.hostname.includes('loom.com')) {
        const parts = u.pathname.split('/').filter(Boolean);
        if (parts[0] === 'share' && parts[1]) {
          return `https://www.loom.com/embed/${parts[1]}`;
        }
      }
      return url;
    } catch (e) {
      return url;
    }
  };

  const base = toEmbedUrl(loomUrl);
  const sep = base.includes('?') ? '&' : '?';
  const embedSrc = `${base}${sep}hideEmbedTopBar=true`;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
      </div>
      <div className="aspect-video bg-black">
        {loomUrl ? (
          <iframe
            src={embedSrc}
            title={title}
            allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
            className="w-full h-full border-0"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
            {noVideoText}
          </div>
        )}
      </div>
    </div>
  );
};

export default function VideoGuides() {
  useGoogleAnalytics();
  const { pageLanguage } = usePageLanguage();
  const t = translations[pageLanguage] || translations.en;
  
  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead 
        title={t.pageTitle}
        description={t.pageDescription}
        lang={pageLanguage}
        canonical="https://scoreboard.tomroethlisberger.ch/videos"
      />
      <HeaderSection />

      <main className="flex-grow">
        <div className="container mx-auto px-4 py-8 max-w-6xl">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">{t.title}</h1>
            <p className="text-gray-600 mt-2">
              {t.subtitle} <a href="/manual" className="text-blue-600 hover:underline">{t.manual}</a>.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {guides.map(g => (
              <VideoCard key={g.id} title={g.title} loomUrl={g.loomUrl} noVideoText={t.noVideo} />
            ))}
          </div>
        </div>
      </main>

      <FooterSection />
    </div>
  );
}
